import Paragraph from "@tiptap/extension-paragraph"

/** 2026-09-17 block-editor design: the base Paragraph node plus a `slot`
 *  attribute naming which editable wording this is (`scope_text`, `basis`,
 *  `statement:<framework_key>:<verdict>`, `monitoring:<framework_key>`) — the
 *  same vocabulary the backend's mc_conformity_blocks module uses for tree
 *  derivation and the save endpoint's allow-list.
 *
 *  Keyboard shortcuts override Tiptap's core `Keymap` extension, which by
 *  default binds Enter to `splitBlock` and Backspace/Delete at a paragraph
 *  boundary to `joinBackward`/`joinForward` — both assume a normal document
 *  of interchangeable paragraphs. Here every paragraph is a fixed, named
 *  slot with a `systemFragment` atom on either side:
 *   - Enter splitting the node would produce two paragraphs sharing one
 *     `slot` attribute (the split copies attrs) — the autosave that reads
 *     "the" paragraph for a slot would then see only one half, silently
 *     dropping whichever text landed in the other.
 *   - `joinBackward`/`joinForward` at a boundary do not "join into" an atom
 *     node the way they would a normal paragraph — ProseMirror's default
 *     behavior there is to select and then delete the neighbouring atom,
 *     destroying a locked system fragment (framework tables, signature,
 *     header) that must never be user-editable content.
 *  So Enter becomes a hard break (the same node the paragraph already
 *  allows), and Backspace/Delete are swallowed at the boundary rather than
 *  reaching for a neighbour — normal mid-text editing is untouched, since
 *  these only intercept the boundary case. */
export const SlotParagraph = Paragraph.extend({
  // Tiptap gives every extension's shortcut-derived keymap its own
  // ProseMirror plugin and tries them in extension-priority order (default
  // 100 for everything, core extensions included) — a tie falls back to
  // registration order, which put the core `Keymap` extension's Backspace/
  // Delete/Enter binding ahead of this node's own, so the core binding won
  // and reached the neighbouring atom first. A higher priority here makes
  // this node's shortcuts run BEFORE the core ones for the same key.
  priority: 1000,

  addAttributes() {
    return {
      slot: {
        default: null,
        parseHTML: (element) => element.getAttribute("data-slot"),
        renderHTML: (attributes) =>
          attributes.slot ? { "data-slot": attributes.slot } : {},
      },
    }
  },

  addKeyboardShortcuts() {
    const insertHardBreak = () =>
      this.editor.commands.first(({ commands }) => [
        () => commands.newlineInCode(),
        () => commands.setHardBreak(),
      ])

    const swallowAtBoundary = (atStart: boolean) => () => {
      const { $from, empty } = this.editor.state.selection
      if (!empty || $from.parent.type.name !== this.name) return false
      const atBoundary = atStart
        ? $from.parentOffset === 0
        : $from.parentOffset === $from.parent.content.size
      // Not our boundary: fall through to whatever handles normal editing
      // (mid-text character deletion is native browser behaviour, never a
      // ProseMirror command, so returning false here is a true no-op).
      return atBoundary
    }

    return {
      Enter: insertHardBreak,
      "Shift-Enter": insertHardBreak,
      "Mod-Enter": insertHardBreak,
      Backspace: swallowAtBoundary(true),
      Delete: swallowAtBoundary(false),
    }
  },
})
